import "./MergeFlow.scss";
import { EmptySpin } from "molecules/ui/EmptySpin/EmptySpin";
import { Flex, Space, theme } from "antd";
import { PatientSummary, PatientSummaryFromPmsId } from "molecules/PatientSummary";
import { SearchPatientForm } from "../SearchPatientForm/SearchPatientForm";
import { UnlinkedIndicator } from "molecules/PatientSummary/UnlinkedIndicator";
import { WarningTwoTone } from "@ant-design/icons";
import type { MergeConfirmationProps, MergeFlowProps, MergeMergeProps, MergeSearchProps } from "./mergeFlow.props";

const { useToken } = theme;

/** Flow for merging unlinked P/RP with PMS Patient. */
export const MergeFlow = ({ mergeState, dispatchMergeAction, unlinkedPrp, mergeFailedMessage }: MergeFlowProps) => {
    const { pmsId, confirmedInitial, confirmedFinal } = mergeState;

    if (!pmsId) {
        // step 1 - select a PMS patient
        return <MergeSearch unlinkedPrp={unlinkedPrp} dispatchMergeAction={dispatchMergeAction} />;
    } else if (confirmedFinal) {
        // step 4 - do the merge
        return <MergeMerge pmsId={pmsId} mergeFailedMessage={mergeFailedMessage} />;
    } else if (confirmedInitial) {
        // step 3 - confirm the merge
        return <MergeFinalConfirmation unlinkedPrp={unlinkedPrp} pmsId={pmsId} />;
    } else {
        // step 2 - PMS patient was selected, but no confirmations passed yet
        return <MergeInitialConfirmation pmsId={pmsId} unlinkedPrp={unlinkedPrp} />;
    }
};

const PreviousPaymentText = () => {
    const { token } = useToken();

    return (
        <Space align="center" style={{ marginBottom: "1em" }}>
            <UnlinkedIndicator large />
            <div style={{ backgroundColor: token.colorBgContainer, padding: "1em" }}>
                This patient has a previous payment request with a balance that was not synced from your practice
                software.
            </div>
        </Space>
    );
};

/** Step 1 of the merge flow. */
const MergeSearch = ({ unlinkedPrp, dispatchMergeAction }: MergeSearchProps) => (
    <Flex vertical={true} gap="middle" className="merge-form">
        <PatientSummary patientData={{ ...unlinkedPrp, isInsuranceCarrier: false }} />
        <PreviousPaymentText />
        <header>To continue, search Patient to Merge:</header>
        <SearchPatientForm
            excludeUnlinked
            onSelectExistingPrp={(prp) => {
                if (prp.pmsId) {
                    dispatchMergeAction({ type: "pmspatient", pmsId: prp.pmsId });
                } else {
                    throw new Error("Search returned non-PMS patient despite filter");
                }
            }}
            onSelectNewPrp={null}
        />
    </Flex>
);

/** Step 2 of the merge flow. */
const MergeInitialConfirmation = ({ unlinkedPrp, pmsId }: MergeConfirmationProps) => (
    <Flex vertical={true} gap="middle" className="merge-form">
        <PatientSummary patientData={{ ...unlinkedPrp, isInsuranceCarrier: false }} />
        <PreviousPaymentText />
        <header>This action will merge the two patient records as one. The details are displayed below:</header>
        <PatientSummaryFromPmsId pmsId={pmsId} />
    </Flex>
);

/** Step 3 of the merge flow. The footer changes during this step. */
const MergeFinalConfirmation = ({ pmsId }: MergeConfirmationProps) => (
    <Flex vertical={true} gap="middle" className="merge-form">
        <PatientSummaryFromPmsId pmsId={pmsId} />
    </Flex>
);

/** Step 4 of the merge flow (component). Calls Merge API immediately (and unconditionally) upon render. */
const MergeMerge = ({ pmsId, mergeFailedMessage }: MergeMergeProps) => {
    const { token } = useToken();

    // show spinner (or error upon failure). success dispatches away to a different state.
    return (
        <Flex vertical={true} gap="middle" className="merge-form">
            <PatientSummaryFromPmsId pmsId={pmsId} />
            {mergeFailedMessage === "" ? (
                <EmptySpin tip="Merging Patients..." />
            ) : (
                <div style={{ fontWeight: "bold" }}>
                    <WarningTwoTone twoToneColor={token.colorWarning} />
                    &nbsp; {mergeFailedMessage}
                </div>
            )}
        </Flex>
    );
};
